import React from "react";
import { ArrowRight, Check, UserCheck } from "lucide-react";

/**
 * ConfirmationView — Constellation-style "order submitted" page shown
 * after the OTP is verified and the lead is created.
 */
export default function ConfirmationView({
  orderNumber,
  form,
  currentPlan,
  estimate,
  address,
  unit,
  city,
  zip,
  esiidParam,
  utilityParam,
}) {
  const monthly = estimate?.annual_kwh
    ? Math.max(0, Math.round((currentPlan.rate / 100) * (estimate.annual_kwh / 12)))
    : null;
  const fullName = `${form.nombre} ${form.apellido}`.trim();
  const fullAddr = [address || `ZIP ${zip}`, unit && `#${unit}`, city, "TX", zip]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="max-w-[760px] mx-auto" data-testid="enroll-success">
      <div className="text-center mb-10">
        <span
          className="grid place-items-center rounded-full mx-auto mb-5 shadow-[0_8px_26px_-10px_rgba(47,122,58,0.5)]"
          style={{
            width: 84,
            height: 84,
            background: "linear-gradient(180deg, rgba(47,122,58,0.18), rgba(47,122,58,0.08))",
            color: "#2f7a3a",
          }}
        >
          <Check className="w-10 h-10" strokeWidth={2.6} />
        </span>
        <h1
          className="display text-velcor-ink leading-[1.05] mb-3"
          style={{ fontSize: "clamp(28px, 4.4vw, 44px)", letterSpacing: "-0.025em", fontWeight: 600 }}
          data-testid="success-title"
        >
          Tu solicitud fue enviada.
          <br />
          ¡Gracias!
        </h1>
        <p className="text-slate-500 text-[14.5px] max-w-[520px] mx-auto leading-relaxed">
          Tu solicitud está en proceso. Un asesor humano revisará los datos y te confirmará la
          activación en menos de 1 hora.
        </p>
      </div>

      <div
        className="rounded-2xl bg-gradient-to-br from-velcor-green/5 via-white to-velcor-green/8 border-2 border-velcor-green/25 p-6 md:p-8 text-center mb-8 relative overflow-hidden"
        data-testid="enroll-order-number"
      >
        <div aria-hidden className="absolute inset-x-0 top-0 h-1 bg-velcor-green" />
        <div className="text-[10.5px] font-extrabold uppercase tracking-[0.22em] text-velcor-green mb-2">
          Tu número de orden
        </div>
        <div
          className="display text-velcor-ink tabular-nums font-mono"
          style={{ fontSize: "clamp(28px, 4.4vw, 40px)", letterSpacing: "-0.01em", fontWeight: 700 }}
        >
          {orderNumber || "—"}
        </div>
        <p className="text-[12px] text-slate-500 mt-3 max-w-[420px] mx-auto leading-snug">
          Guarda este número. Lo usaremos en cualquier comunicación contigo sobre tu activación.
        </p>
      </div>

      <div className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
        <ConfirmSection label="Solicitud enviada para" testid="success-block-recipient">
          <div className="font-bold text-velcor-ink text-[15.5px]">{fullName}</div>
          <div className="text-[13px] text-slate-600 mt-0.5">{fullAddr}</div>
          {esiidParam && (
            <div className="text-[11.5px] text-slate-500 mt-1 font-mono">ESIID: {esiidParam}</div>
          )}
          {utilityParam && (
            <div className="text-[11.5px] text-slate-500 mt-0.5">
              TDU: <strong className="text-slate-700">{utilityParam}</strong>
            </div>
          )}
        </ConfirmSection>

        {form.referral_code && (
          <ConfirmSection label="Código especial usado" testid="success-block-referral">
            <div className="inline-flex items-center gap-2 rounded-lg bg-velcor-green/10 px-3 py-1.5 ring-1 ring-velcor-green/30">
              <UserCheck className="w-3.5 h-3.5 text-velcor-green" />
              <span className="font-mono font-bold text-velcor-green text-[14px] tracking-wider">
                {form.referral_code}
              </span>
            </div>
            <p className="text-[12px] text-slate-500 mt-2 leading-snug">
              Tu amigo recibirá <strong className="text-velcor-green">$50</strong> de crédito tras
              tu primera factura pagada.
            </p>
          </ConfirmSection>
        )}

        <ConfirmSection label="Tu plan seleccionado" testid="success-block-plan">
          <div className="font-bold text-velcor-ink text-[15.5px]">{currentPlan.name}</div>
          <div className="text-[13px] text-slate-600 mt-0.5">
            <strong className="text-velcor-ink tabular-nums">{currentPlan.rate}¢ / kWh</strong> ·{" "}
            {currentPlan.term}
          </div>
          {form.fecha_inicio && (
            <div className="text-[11.5px] text-slate-500 mt-2">
              Inicio de servicio: <strong className="text-slate-700">{form.fecha_inicio}</strong>
            </div>
          )}
        </ConfirmSection>

        <ConfirmSection label="Tarifas adicionales" testid="success-block-fees">
          <ul className="space-y-1.5 text-[13px]">
            <FeeRow label="DEPÓSITO DE SEGURIDAD" value="$0 · waiver disponible vía SSN" />
            <FeeRow label="CARGOS TDU" value="Pass-through · regulados por PUC" />
            <FeeRow
              label="ELECTRICIDAD"
              value={monthly !== null ? `~$${monthly} / mes estimado` : "Calculado tras activación"}
            />
            <FeeRow label="CARGO MENSUAL BASE" value="$0" />
          </ul>
        </ConfirmSection>
      </div>

      <div className="mt-8 rounded-2xl bg-slate-50/70 border border-slate-200 p-5 md:p-6 text-center">
        <p className="text-[13px] text-slate-600 leading-relaxed">
          Recibirás un correo de confirmación en{" "}
          <strong className="text-velcor-ink">{form.email}</strong> con los documentos EFL, YRAC y
          Términos. Si necesitas ayuda, escríbenos a{" "}
          <a
            href="mailto:support@velcorenergy.com"
            className="text-velcor-green font-bold hover:underline"
          >
            support@velcorenergy.com
          </a>{" "}
          o llámanos al{" "}
          <a href="tel:+18007156516" className="text-velcor-green font-bold hover:underline">
            (800) 715-6516
          </a>
          .
        </p>
      </div>

      <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
        <a
          href="/portal/login"
          className="btn-green h-12 px-6 inline-flex items-center gap-2"
          data-testid="success-portal-login"
        >
          Acceder a mi portal <ArrowRight className="w-4 h-4" />
        </a>
        <a
          href="/"
          className="h-12 px-6 inline-flex items-center justify-center rounded-full border-2 border-slate-200 hover:border-slate-300 text-slate-700 font-extrabold text-[13.5px]"
          data-testid="success-home"
        >
          Volver al inicio
        </a>
      </div>
    </div>
  );
}

function ConfirmSection({ label, children, testid }) {
  return (
    <div className="border-b border-slate-100 last:border-b-0 px-5 md:px-7 py-5" data-testid={testid}>
      <div className="text-[10.5px] font-extrabold uppercase tracking-[0.18em] text-velcor-green mb-2.5">
        {label}
      </div>
      {children}
    </div>
  );
}

function FeeRow({ label, value }) {
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-2">
      <span className="text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-velcor-green">
        {label}
      </span>
      <span className="text-[13px] text-slate-600 font-semibold">{value}</span>
    </li>
  );
}
